import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from 'prop-types';
import { Button, Col, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Rating from "react-rating";
import { Remark } from 'react-remark';
import dayjs from "dayjs";
import { starsOf } from "./conversationStats";
import './RowDetailModal.scss';

// Bootstrap puts every modal on 1055, so a detail opened from inside the
// chart drill-down would only win on DOM order. Pin it above instead.
const STACKED_MODAL_Z = 1065;

// The answer panel is tinted by how it scored, so a poor answer is obvious while
// reviewing. On a 1-5 scale that needs a threshold: 1-2 reads as negative, 3 as
// neutral, 4-5 as positive.
const answerBgFor = stars => {
    if (stars === null || stars === 3) return 'bg-100';
    return stars <= 2 ? 'bg-soft-danger' : 'bg-soft-success';
};

// Row click doubles as the way to select and copy question text, so don't
// hijack a click the user made to finish a selection.
const isSelecting = () => !!window.getSelection()?.toString();

export const useRowDetail = () => {
    const [detail, setDetail] = useState(null);

    // AdvanceTable calls handleClickRow(event, row, ...).
    const openDetail = useCallback((_event, row) => {
        if (isSelecting()) return;
        setDetail(row?.original ?? null);
    }, []);

    const closeDetail = useCallback(() => setDetail(null), []);

    return { detail, openDetail, closeDetail };
};

const Field = ({ label, children, xs = 6, m = 3 }) => (
    <Col xs={xs} md={m} className="mb-3">
        <h5 className="text-700 mb-1">{label}</h5>
        <div className="text-800">{children}</div>
    </Col>
);

Field.propTypes = {
    label: PropTypes.string,
    children: PropTypes.node,
    xs: PropTypes.number,
    m: PropTypes.number,
};

const RowDetailModal = ({ detail, onHide }) => {
    const stars = detail ? starsOf(detail) : null;

    const created = useMemo(() => {
        const date = detail?.createdDate ? dayjs(detail.createdDate) : null;
        return date?.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : '—';
    }, [detail]);

    // Remounted per row, so remark re-parses instead of showing the last answer.
    const answer = useMemo(() => {
        const markdown = String(detail?.answer ?? '').trim();
        return markdown ? <Remark>{markdown}</Remark> : <span className="text-600">No answer recorded.</span>;
    }, [detail]);

    return (
        <Modal
            show={!!detail}
            onHide={onHide}
            size="lg"
            centered
            scrollable
            style={{ zIndex: STACKED_MODAL_Z }}
            backdropClassName="elvis-detail-backdrop"
        >
            <Modal.Header closeButton>
                <Modal.Title>Question detail</Modal.Title>
            </Modal.Header>
            {detail && (
                <Modal.Body>
                    <Row className="g-2">
                        <Field label="Asked by">{detail.user || 'Unknown'}</Field>
                        <Field label="Created">{created}</Field>
                        <Field label="Knowledge">
                            {detail.knowledgeName || "No match"}
                        </Field>
                        <Field label="Rating">
                            {stars === null
                                ? <span className="text-600">Unrated</span>
                                : <Rating
                                    readonly
                                    initialRating={stars}
                                    fullSymbol={<FontAwesomeIcon icon="star" className="text-primary me-1" />}
                                    emptySymbol={<FontAwesomeIcon icon="star" className="text-300 me-1" />}
                                />}
                        </Field>
                    </Row>
                    <Row className="g-2">
                        <Field label="Question">{detail.question || "—"}</Field>
                    </Row>
                    <Row className="g-2">
                        <Field label="Answer" xs={12} m={12}>
                            <div className={`border rounded p-3 fs--1 overflow-auto elvis-answer ${answerBgFor(stars)}`}>
                                {answer}
                            </div>
                        </Field>
                    </Row>
                </Modal.Body>
            )}
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

RowDetailModal.propTypes = {
    detail: PropTypes.shape({
        question: PropTypes.string,
        answer: PropTypes.string,
        user: PropTypes.string,
        createdDate: PropTypes.string,
        knowledgeName: PropTypes.string,
        rating: PropTypes.number
    }),
    onHide: PropTypes.func
};

export default memo(RowDetailModal);
